import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Recycle, LogIn, Eye, EyeOff, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPendingApproval(false);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 403 || detail.includes('patvirtinimo')) {
        setPendingApproval(true);
      } else {
        toast({ title: 'Klaida', description: detail || 'Neteisingi prisijungimo duomenys', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Recycle className="w-8 h-8 text-[#00C300]" />
            <span className="text-lg font-bold text-gray-900">taršosmokestis.lt</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Neturite paskyros?</span>
            <Link to="/register">
              <Button className="bg-[#00C300] hover:bg-[#00A300] text-white rounded-full px-6 font-semibold">
                Registruotis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex w-16 h-16 bg-green-100 rounded-2xl items-center justify-center mb-4">
              <Recycle className="w-8 h-8 text-[#00C300]" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900">Sveiki sugrįžę</h1>
            <p className="text-gray-500 mt-2">Prisijunkite prie savo paskyros</p>
          </div>

          <Card className="bg-white rounded-2xl border border-gray-100 shadow-lg">
            <CardContent className="p-8">
              {pendingApproval && (
                <div className="mb-5 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-3">
                  <Clock className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">Paskyra laukia patvirtinimo</p>
                    <p className="text-xs text-yellow-700 mt-0.5">
                      Administratorius dar nepatvirtino jūsų paskyros. Susisiekite su administratoriumi arba palaukite patvirtinimo el. laiško.
                    </p>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">El. paštas</Label>
                  <Input
                    type="email"
                    placeholder="jusu@imone.lt"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl border-gray-200 focus:border-[#00C300] focus:ring-[#00C300]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Slaptažodis</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-[#00C300] focus:ring-[#00C300] pr-12"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#00C300] hover:bg-[#00A300] text-white rounded-full font-bold text-base">
                  <LogIn className="w-5 h-5 mr-2" />
                  {loading ? 'Jungiamasi...' : 'Prisijungti'}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <p className="text-gray-500 text-sm">
                  Neturite paskyros?{' '}
                  <Link to="/register" className="text-[#00C300] font-semibold hover:underline">Registruotis</Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
